import React, { useContext, useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { Button, Form, Space, Table, Modal, Pagination, Input, Upload, notification, Flex, Tooltip, DatePicker, Select, InputNumber } from 'antd'
import dayjs from 'dayjs'
import { apiRequest } from '../helpers'
import { CCard } from '@coreui/react'
import { BsPencil, BsTrash3 } from 'react-icons/bs'
import { FaRegStopCircle, FaRegPlayCircle, FaSpinner } from 'react-icons/fa'
import { FiUpload, FiDownload } from 'react-icons/fi'
import { MdOutlineRemoveRedEye } from 'react-icons/md'
import FormInputs from '../components/FormInputs'
import { apiDownloadRequest } from '../helpers/apiRequest'
import { useLanguage } from './context/LanguageContext'
import { AuthorizationContext } from './auth/AuthProvider'
import { Content } from 'antd/es/layout/layout'
import DropdownButton from 'antd/es/dropdown/dropdown-button'
import { DownOutlined, CopyOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons'
import { useMsal } from '@azure/msal-react'
import * as msal from '@azure/msal-browser'
import { FaRegCircleCheck } from 'react-icons/fa6'
import { createStyles } from 'antd-style'

const TableContent = forwardRef(
  (
    {
      url,
      item,
      items,
      parentParse,
      type,
      onRowSelect,
      onRowsSelectionChange,
      refreshData,
      onRefreshData,
      extensions,
      renderExpandedRow,
      renderRowContent,
      rowClassName,
      rowHasCheckbox = true,
      extraHeaderButtons = null,
      hideRefresh = false,
      defaultSortColumn = '',
      defaultSortDirection = '',
      customFilters = null,
      customFilterFn = null,
      customFilterParams = null,
      customFilterUrlBuilder = null,
      onPaginationChange = null,
    },
    ref
  ) => {
    //eslint-disable-next-line
    const itemUrl = process.env.REACT_APP_API + url
    const [form] = Form.useForm()
    const [data, setData] = useState([])
    const [inputs, setInputs] = useState([])
    const [columns, setColumns] = useState([])
    const [permissions, setPermissions] = useState([])
    const [targetKeys, setTargetKeys] = useState([])
    const [selectedKeys, setSelectedKeys] = useState([])
    const [selectedRowKeys, setSelectedRowKeys] = useState([])
    const [entityId, setEntityId] = useState(null)
    const [skipMeta, setSkipMeta] = useState(false)
    const [refreshTable, setRefreshTable] = useState(0)
    const [showDeleteModal, setDeleteModal] = useState(false)
    const [showUpdateModal, setUpdateModal] = useState(false)
    const [showCreateModal, setCreateModal] = useState(false)
    const [usernameMS, setUsernameMS] = useState(null)
    const { userData } = useContext(AuthorizationContext)
    const [loadingTest, setLoadingTest] = useState({})

    const useStyle = createStyles(({ css, token }) => {
      const { antCls } = token
      return {
        customTable: css`
          ${antCls}-table {
            ${antCls}-table-container {
              ${antCls}-table-body,
              ${antCls}-table-content {
                scrollbar-width: thin;
                scrollbar-color: #eaeaea transparent;
                scrollbar-gutter: stable;
              }
            }
          }
        `,
      }
    })
    const { styles } = useStyle()

    // Function to update a specific record in the table data
    const updateRecord = useCallback((recordId, updatedFields) => {
      setData(prevData => prevData.map(record => (record.id === recordId || record.key === recordId ? { ...record, ...updatedFields } : record)))
    }, [])

    // Function to reset all table filters
    const resetAllFilters = useCallback(() => {
      setFilter({
        search: '',
        sortColumn: defaultSortColumn,
        sortDirection: defaultSortDirection,
        filters: [],
        columnFilters: {},
      })
      setLocalColumnFilters({})
      setPagination(prev => ({
        ...prev,
        current: 1,
      }))
      setRefreshTable(prev => prev + 1)
    }, [defaultSortColumn, defaultSortDirection])

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      refreshTable: () => setRefreshTable(prev => prev + 1),
      updateRecord,
      resetAllFilters,
      getTotalCount: () => pagination.total,
    }))

    useEffect(() => {
      if (refreshData) {
        getTableContent(skipMeta)
      }

      /** At refresh verifies if the ms_profile exists in sessionStorage and if it does, sets the state.*/
      const profile = JSON.parse(sessionStorage.getItem('ms_profile'))
      if (!profile) return

      setUsernameMS(profile.username)

      //eslint-disable-next-line
    }, [refreshData, url])

    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 10,
      total: 1,
    })

    const [filter, setFilter] = useState({
      search: '',
      sortColumn: defaultSortColumn,
      sortDirection: defaultSortDirection,
      filters: [],
      columnFilters: {},
    })

    // Local state for input values (for immediate UI update)
    const [localColumnFilters, setLocalColumnFilters] = useState({})
    const debounceTimeouts = useRef({})
    const searchInputRefs = useRef({})

    const [buttons, setButtons] = useState({
      create: false,
      delete: false,
      update: false,
      play: false,
      stop: false,
      download: false,
      upload: false,
      view: false,
      test: false,
    })

    const { Search } = Input

    const languageContext = useLanguage()

    // Handle row selection changes
    const handleRowSelectionChange = useCallback((newSelectedRowKeys, selectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys)
      if (onRowsSelectionChange) {
        onRowsSelectionChange(selectedRows)
      }
    }, [onRowsSelectionChange])

    // Create rowSelection configuration
    const rowSelection = rowHasCheckbox ? {
      selectedRowKeys,
      onChange: handleRowSelectionChange,
    } : undefined

    useEffect(() => {
      getTableContent(skipMeta)
      //eslint-disable-next-line
    }, [pagination.current, pagination.pageSize, filter.search, filter.sortColumn, filter.sortDirection, filter.filters, refreshTable])

    // Trigger data refresh when custom filter parameters change
    useEffect(() => {
      if (customFilterParams && skipMeta) {
        // Reset to page 1 when filters change
        setPagination(prev => ({
          ...prev,
          current: 1,
        }))
        getTableContent(true)
      }
      //eslint-disable-next-line
    }, [customFilterParams])

    // Aplică filtrarea custom la datele din tabel, dacă există funcție primită din părinte
    const displayedData = customFilterFn ? customFilterFn(data) : data

    // Separate useEffect for column filters to prevent infinite loop
    useEffect(() => {
      if (skipMeta && Object.keys(filter.columnFilters).length >= 0) {
        getTableContent(true)
      }
      //eslint-disable-next-line
    }, [filter.columnFilters])

    useEffect(() => {
      /**Get data with columns when language change */
      if (!skipMeta) return
      getTableContent()

      //eslint-disable-next-line
    }, [userData.language])

    // Cleanup debounce timeouts on unmount
    useEffect(() => {
      return () => {
        Object.values(debounceTimeouts.current).forEach(timeout => {
          if (timeout) clearTimeout(timeout)
        })
      }
    }, [])

    // Helper functions for Ant Design filtering
    const handleSearch = (selectedKeys, confirm) => {
      confirm()
    }

    const handleReset = (clearFilters, columnKey = null) => {
      clearFilters()
      if (columnKey) {
        setLocalColumnFilters(prev => ({
          ...prev,
          [columnKey]: undefined,
        }))
      }
    }

    const getColumnSearchProps = column => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="ant-table-filter-dropdown" style={{ padding: 8 }}>
          <Input
            ref={node => {
              searchInputRefs.current[column.key] = node
            }}
            placeholder={`${languageContext.t('Search')} ${column.label}`}
            value={selectedKeys[0]}
            onChange={e => {
              setSelectedKeys(e.target.value ? [e.target.value] : [])
              // Update local state for immediate UI feedback only
              setLocalColumnFilters(prev => ({
                ...prev,
                [column.key]: e.target.value,
              }))
            }}
            onPressEnter={() => handleSearch(selectedKeys, confirm)}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters()
                setLocalColumnFilters(prev => ({
                  ...prev,
                  [column.key]: undefined,
                }))
                // Automatically confirm after clearing to trigger search
                setTimeout(() => confirm(), 0)
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilterDropdownVisibleChange: visible => {
        if (visible) {
          setTimeout(() => searchInputRefs.current[column.key]?.select(), 100)
        }
      },
    })

    const getColumnDateProps = column => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="ant-table-filter-dropdown" style={{ padding: 8, width: 300 }}>
          <div style={{ marginBottom: 8 }}>
            <DatePicker.RangePicker
              value={selectedKeys[0] ? selectedKeys[0].map(date => (date ? dayjs(date) : null)) : [null, null]}
              onChange={(dates, dateStrings) => {
                if (dates && dates.length === 2) {
                  setSelectedKeys([dateStrings])
                } else {
                  setSelectedKeys([])
                }
              }}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          </div>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm()
              }}
              size="small"
              style={{ width: 90 }}
            >
              OK
            </Button>
            <Button
              onClick={() => {
                clearFilters()
                handleReset(clearFilters)
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    })

    const getColumnSelectProps = column => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const allValues = column.options?.map(option => option.value) || []
        const isAllSelected = selectedKeys.length === allValues.length
        const isIndeterminate = selectedKeys.length > 0 && selectedKeys.length < allValues.length

        const handleSelectAll = () => {
          setSelectedKeys(allValues)
        }

        const handleClearAll = () => {
          setSelectedKeys([])
        }

        const handleOptionChange = (value, checked) => {
          if (checked) {
            setSelectedKeys([...selectedKeys, value])
          } else {
            setSelectedKeys(selectedKeys.filter(key => key !== value))
          }
        }

        return (
          <div className="ant-table-filter-dropdown" style={{ padding: 8, minWidth: 200 }}>
            <div style={{ marginBottom: 8, maxHeight: 250, overflow: 'auto' }}>
              {/* Select All / Clear All options */}
              <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 8, marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', padding: '4px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={checkbox => {
                      if (checkbox) checkbox.indeterminate = isIndeterminate
                    }}
                    onChange={e => {
                      if (e.target.checked) {
                        handleSelectAll()
                      } else {
                        handleClearAll()
                      }
                    }}
                    style={{ marginRight: 8 }}
                  />
                  <strong>
                    {isAllSelected ? languageContext.t('Sterge tot') || 'Sterge tot' : languageContext.t('Selecteaza tot') || 'Selecteaza tot'}
                  </strong>
                </label>
              </div>

              {/* Individual options */}
              {column.options?.map(option => (
                <label key={option.value} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(option.value)}
                    onChange={e => handleOptionChange(option.value, e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  confirm()
                }}
                size="small"
                style={{ width: 90 }}
              >
                OK
              </Button>
              <Button
                onClick={() => {
                  clearFilters()
                  handleReset(clearFilters)
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        )
      },
      filterIcon: filtered => <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    })

    const getColumnNumberProps = column => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="ant-table-filter-dropdown" style={{ padding: 8, width: 280 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Min:</span>
              <InputNumber
                placeholder={`${languageContext.t('Min')} ${column.label}`}
                value={selectedKeys[0] && Array.isArray(selectedKeys[0]) ? selectedKeys[0][0] : null}
                onChange={value => {
                  const currentMax = selectedKeys[0] && Array.isArray(selectedKeys[0]) ? selectedKeys[0][1] : null
                  if (value !== null || currentMax !== null) {
                    setSelectedKeys([[value, currentMax]])
                  } else {
                    setSelectedKeys([])
                  }
                }}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Max:</span>
              <InputNumber
                placeholder={`${languageContext.t('Max')} ${column.label}`}
                value={selectedKeys[0] && Array.isArray(selectedKeys[0]) ? selectedKeys[0][1] : null}
                onChange={value => {
                  const currentMin = selectedKeys[0] && Array.isArray(selectedKeys[0]) ? selectedKeys[0][0] : null
                  if (value !== null || currentMin !== null) {
                    setSelectedKeys([[currentMin, value]])
                  } else {
                    setSelectedKeys([])
                  }
                }}
                style={{ width: '100%' }}
              />
            </div>
          </div>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm()
              }}
              size="small"
              style={{ width: 90 }}
            >
              OK
            </Button>
            <Button
              onClick={() => {
                clearFilters()
                setLocalColumnFilters(prev => ({
                  ...prev,
                  [column.key]: undefined,
                }))
                // Automatically confirm after clearing to trigger search
                setTimeout(() => confirm(), 0)
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    })

    useEffect(() => {
      if (!form) return
      form.resetFields()
      //eslint-disable-next-line
    }, [showUpdateModal, showCreateModal])

    const localParse = (items, isTable = false) => {
      if (!items) return {}
      let processed = {}

      Object.keys(items).forEach(key => {

        // Recursively find input definition for this key
        function findInputByKey(inputsArr, searchKey) {
          if (!inputsArr) return undefined;
          for (const input of inputsArr) {
            if (input.key === searchKey) return input;
            if (input.children) {
              // children can be an object or array
              const childrenArr = Array.isArray(input.children)
                ? input.children
                : Object.values(input.children);
              const found = findInputByKey(childrenArr, searchKey);
              if (found) return found;
            }
          }
          return undefined;
        }

        let itemsItem = findInputByKey(inputs, key);

        //added && item === false to the condition because it was showing active only if true
        if (!items[key] && item === false) {
          processed[key] = ''
        } else if (key === 'config') {
          processed[key] = JSON.stringify(items[key], null, '\t')
        } else if (itemsItem && itemsItem.type === 'json') {
          // Keep JSON fields as objects for Form.Item's getValueProps to handle
          // This allows getValueProps in FormInputs to convert object -> string for the editor
          processed[key] = items[key] || null
        } else if (key === 'createdBy') {
          processed.createdBy = items['user']?.email
        } else if (key === 'id') {
          processed.key = items[key]
          processed[key] = items[key]
        } else if (key === 'value') {
          for (const objKey in items[key]) {
            processed[objKey] = items[key][objKey]
          }
        } else if (key === 'ai') {
          /**Verifies if key is equal to 'smtp' and creates a new key named 'aiActive' and 'aiId' that corresponds with the columns from backend to verify the status of aiConnector in EmailAgent*/
          processed.aiActive = items[key]?.active
          processed.aiId = items[key]?.name
        } else if (key === 'emailAccount') {
          /**Verifies if key is equal to 'emailAccount' and creates a new key named 'emailAccountActive' that corresponds with the columns from backend to verify the status of emailAccount in EmailAgent*/
          processed.emailAccountActive = items[key]?.active
        } else if (isTable && items[key] && items[key].name) {
          if (processed[key + 'Id']) processed[key + 'Id'] = items[key].name
          processed[key] = items[key].name
        } else if (itemsItem && itemsItem.type === 'datetime') {
          processed[key] = dayjs(items[key])
        } else {
          processed[key] = items[key]
        }

        // Recursively parse children if present (object)
        if (items.children) {
          processed = { ...processed, ...localParse(items.children) }
        }
      })

      return processed
    }

    const parse = parentParse ? parentParse : localParse

    const getTableContent = async (skip = false) => {
      const url = getPaginationUrl(skip)
      const result = await apiRequest(url, 'GET')
      if (!result?.ok || !result?.data?.data) return

      if (result.data.hasOwnProperty('inputs') && result.data.hasOwnProperty('columns') && result.data.hasOwnProperty('actions')) {
        setColumns(result.data.columns)
        setInputs(result.data.inputs)
        setPermissions(result.data.permissions)
        setButtons({
          create: result.data.actions.includes('create'),
          delete: result.data.actions.includes('delete'),
          update: result.data.actions.includes('update'),
          copy: result.data.actions.includes('copy'),
          download: type === 'kit' && result.data.actions.includes('read'),
          upload: result.data.actions.includes('upload'),
          play: result.data.actions.includes('run'),
          stop: result.data.actions.includes('run'),
          view: type === 'task' && result.data.actions.includes('read'),
          test: result.data.actions.includes('test'),
        })
        setSkipMeta(true)
      }

      const newPagination = {
        current: result.data.meta.currentPage,
        pageSize: result.data.meta.itemsPerPage,
        total: result.data.meta.totalItems,
      }

      // Calculate total pages
      const totalPages = Math.ceil(newPagination.total / newPagination.pageSize)

      let dataToProcess = result.data.data

      // If current page is beyond available pages, adjust to last page
      if (newPagination.current > totalPages && totalPages > 0) {
        newPagination.current = totalPages
        // Refetch data for the correct page
        const adjustedUrl = getPaginationUrl(skip).replace(`page=${result.data.meta.currentPage}`, `page=${totalPages}`)
        const adjustedResult = await apiRequest(adjustedUrl, 'GET')
        if (adjustedResult?.ok && adjustedResult?.data?.data) {
          dataToProcess = adjustedResult.data.data
          newPagination.current = adjustedResult.data.meta.currentPage
        }
      }

      setPagination(prevPagination => {
        const updatedPagination = {
          ...prevPagination,
          ...newPagination,
        }

        // Notify parent component of pagination changes
        if (onPaginationChange) {
          onPaginationChange(updatedPagination)
        }

        return updatedPagination
      })

      const r = dataToProcess
      const c = result?.data?.columns
      /** Return current values if is not mobile */
      if (window.innerWidth > 400) {
        setData(r.map(item => parse(item, true)))
        return
      }

      /** Add labels to value in case it's mobile */

      if ((c?.length, r?.length)) {
        const dataForMobile = r.map(item => {
          const obj = parse(item, true)
          for (const key of Object.keys(obj)) {
            if (!c) break
            const col = c.find(item => item.key === key)
            if (col) obj[key] = col.label + ': ' + obj[key]
          }
          return obj
        })
        setData(dataForMobile)
      }
    }

    const getById = async id => {
      const result = await apiRequest(`${itemUrl}/${id}`, 'GET')
      if (!result.ok) return
      const transferComponent = form.getFieldsValue('permissions')

      if (transferComponent) {
        const targetPermissions = result?.data?.permissions?.map(permission => permissions?.indexOf(permission))

        setTargetKeys(targetPermissions)
      }

      const parsed = parse(result.data)
      form.setFieldsValue(parsed)
    }

    const handleDelete = async id => {
      const result = await apiRequest(`${itemUrl}/${id}`, 'DELETE')
      if (!result.ok) return
      setRefreshTable(prev => prev + 1)
      setDeleteModal(false)
      setEntityId(null)
    }

    const handleDownload = async (id, fileName) => {
      await apiDownloadRequest(`${itemUrl}/download/${id}`, fileName)
    }

    const uploadProps = id => {
      return {
        name: 'file',
        action: `${itemUrl}/${id}`,
        withCredentials: true,
        onChange(info) {
          if (info.file.status === 'error') {
            notification.error({
              message: 'System error!',
              description: 'There was a problem uploading this file!',
            })
          }
          if (info.file.status === 'done') {
            notification.success({
              message: 'File uploaded',
              description: 'Your file was uploaded successfully!',
            })
          }
          setRefreshTable(prev => prev + 1)
        },
      }
    }

    const processValues = (inputs, values) => {
      inputs.forEach(input => {
        if (input.type === 'checkbox' || input.type === 'switch') {
          values[input.name] = !!values[input.name]
        } else if (input.type === 'number') {
          values[input.name] = parseInt(values[input.name] || 0)
        } else if (input.type === 'decimal') {
          values[input.name] = parseFloat(values[input.name] || 0)
        } else if (input.type === 'json') {
          // The value is already normalized to an object by Form.Item in FormInputs
          // If it's null, undefined, or empty string, set to null
          if (!values[input.name] || values[input.name] === '') {
            values[input.name] = null
          }
          // If it's already an object, keep it as is (already normalized)
          // If somehow it's still a string, try to parse it
          else if (typeof values[input.name] === 'string') {
            try {
              values[input.name] = JSON.parse(values[input.name])
            } catch (e) {
              // If parsing fails, set to null
              console.error(`Failed to parse JSON for field ${input.name}:`, e)
              values[input.name] = null
            }
          }
        }
        if (input.children) processValues(input.children, values)
      })
    }

    const save = async values => {
      processValues(inputs, values)

      /**If the values object contains the key 'permissions', add the permissions state to values */
      if (Object.keys(values).includes('permissions')) {
        values.permissions = targetKeys.map(item => permissions[item])
      }

      /**If the values object contains the key 'storageIds' and it's not already a string, stringify it */
      if (Object.keys(values).includes('storageIds') && values.storageIds && !Array.isArray(values.storageIds)) {
        // If it's a single value like "url-1", convert it to an array and stringify
        values.storageIds = JSON.stringify([values.storageIds])
      } else if (Object.keys(values).includes('storageIds') && Array.isArray(values.storageIds)) {
        // If it's already an array, stringify it
        values.storageIds = JSON.stringify(values.storageIds)
      }

      /**If the values object contains the key 'folder', add also the username from sessionStorage to values for storing in DB*/
      if (Object.keys(values).includes('folder')) {
        const msProfile = JSON.parse(sessionStorage.getItem('ms_profile'))
        const refreshTokenItem = Object.keys(sessionStorage).filter(item => item.includes('refreshtoken'))

        values = {
          ...values,
          username: msProfile.username,
          refreshTokenData: JSON.parse(sessionStorage.getItem(refreshTokenItem)),
        }
      }

      const apiUrlEndpoint = entityId ? `${itemUrl}/${entityId}` : `${itemUrl}`
      const method = entityId ? 'PATCH' : 'POST'
      const result = await apiRequest(apiUrlEndpoint, method, values)
      if (!result.ok) return
      setRefreshTable(prev => prev + 1) // Refresh table
      setEntityId(null)
      setTargetKeys(null)
      setUpdateModal(false)
      setCreateModal(false)
      form.resetFields()
      if (onRefreshData) onRefreshData() // Trigger refresh of recruiter details component
    }

    const toggleTask = async record => {
      const result = await apiRequest(`${itemUrl}/${record.key}`, 'PATCH', {
        active: record.active === 'Off',
      })
      if (!result.ok) return
      setRefreshTable(prev => prev + 1) // Refresh table
    }

    const handleView = id => {
      /**Verifica daca utilizatorul are in profile permisiunea de result-read */
      // Use userData from AuthorizationContext instead of sessionStorage
      if (!userData?.permissions?.includes('result-read')) {
        notification.error({
          message: 'System error!',
          description: 'Unauthorized!',
        })
      } else {
        /** Trimitere utilizator la pagina de results pentru task-ul dorit*/
        window.location.assign(`/results/${id}`)
      }
    }

    const handleTest = async id => {
      setLoadingTest(prev => ({ ...prev, [id]: true }))
      const result = await apiRequest(`${itemUrl}/test/${id}`, 'GET')
      setLoadingTest(prev => ({ ...prev, [id]: false }))
      console.log(result)
      // if (result.ok) {
      //   notification.success({
      //     message: 'Test Successful'
      //   })
      // } else {
      //   notification.error({
      //     message: 'Test Failed',
      //     description: result.message,
      //   })
      // }
    }

    const getTableColumns = columns => {
      if (!columns?.length) return []

      let list = []
      for (let col of columns) {
        const column = {
          title: col.label,
          dataIndex: col.name,
          key: col.name,
          sorter: true,
          render: text => (
            <div className="cell-content" style={{cursor: 'default'}}>
              {typeof text === 'boolean'
                ? text
                  ? '✅'
                  : '❌'
                : typeof text === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(text)
                  ? dayjs(text).format('DD/MM/YYYY') // Format date as dd/mm/yyyy for Romania
                  : col.type === 'amount'
                    ? new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(Number(text))
                    : text === null
                      ? ''
                      : String(text)}
              {typeof text !== 'boolean' && text !== null && String(text) !== '' && (
                <Tooltip title={languageContext.t('CopyClipboard')}>
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    className="copy-button"
                    onClick={e => {
                      e.stopPropagation()
                      try {
                        navigator.clipboard.writeText(text)
                        notification.success({
                          message: languageContext.t('Copied'),
                          duration: 1,
                        })
                      } catch (error) {
                        notification.error({
                          message: languageContext.t('Copy Failed'),
                          description: error.message,
                        })
                      }
                    }}
                  />
                </Tooltip>
              )}
            </div>
          ),
        }

        // Add filtering properties based on column type
        if (col.type === 'text') {
          Object.assign(column, getColumnSearchProps(col))
        } else if (col.type === 'date') {
          Object.assign(column, getColumnDateProps(col))
        } else if (col.type === 'number' || col.type === 'amount') {
          Object.assign(column, getColumnNumberProps(col))
        } else if (col.type === 'select') {
          Object.assign(column, getColumnSelectProps(col))
        } else {
          // Default to text search for unknown types
          Object.assign(column, getColumnSearchProps(col))
        }

        if (col.options) column.filters = col.options
        list.push(column)
      }

      /**Cel putin un buton cu exceptia create trebuie sa fie true pentru a aduga coloana de actiuni */
      let showAction = false

      for (const key of Object.keys(buttons)) {
        if (['create', 'read'].includes(key)) continue
        if (buttons[key]) {
          showAction = true
          break
        }
      }

      if (!showAction) return list
      list.push({
        title: languageContext.t('Action'),
        className: 'table-action-column',
        key: 'action',
        fixed: 'right',
        render: (_, record) => (
          <div className="icons-container">
            {buttons.update && (
              <BsPencil
                onClick={e => {
                  e.stopPropagation()
                  setUpdateModal(true)
                  setEntityId(record.key)
                  getById(record.key)
                }}
                title={languageContext.t('Update')}
                size={20}
                className="pencil-icon"
              />
            )}
            {buttons.delete && (
              <BsTrash3
                onClick={e => {
                  e.stopPropagation()
                  setDeleteModal(true)
                  setEntityId(record.key)
                }}
                title={languageContext.t('Delete')}
                size={20}
                className="delete-icon"
              />
            )}
            {buttons.download && !!record.originalName && (
              <FiDownload
                onClick={e => {
                  e.stopPropagation()
                  handleDownload(record.key, record.originalName)
                }}
                title={languageContext.t('Download')}
                size={20}
                className="download-icon"
              />
            )}
            <Upload {...uploadProps(record.key)} showUploadList={false}>
              {buttons.upload && <FiUpload title={languageContext.t('Upload')} size={20} className="upload-icon" />}
            </Upload>
            {buttons.play && record.active === 'Off' && (
              <FaRegPlayCircle
                onClick={e => {
                  e.stopPropagation()
                  toggleTask(record)
                }}
                title={languageContext.t('Start')}
                size={20}
                className="play-icon"
              />
            )}
            {buttons.stop && record.active === 'On' && (
              <FaRegStopCircle
                onClick={e => {
                  e.stopPropagation()
                  toggleTask(record)
                }}
                title={languageContext.t('Stop')}
                size={20}
                className="stop-icon"
              />
            )}
            {buttons.view && (
              <MdOutlineRemoveRedEye
                title={languageContext.t('View')}
                size={25}
                onClick={e => {
                  e.stopPropagation()
                  handleView(record.key)
                }}
                className="view-icon"
              />
            )}
            {buttons.test && (
              <FaRegCircleCheck
                onClick={e => {
                  e.stopPropagation()
                  handleTest(record.key)
                }}
                title={languageContext.t('Test')}
                size={20}
                className={`test-icon ${loadingTest[record.key] ? 'rotating' : ''}`}
              />
            )}
            {extensions?.renderAdditionalExtensions && extensions.renderAdditionalExtensions(record)}
            {/* {buttons.log && (
            <MdOutlineRemoveRedEye
              title={languageContext.t('View')}
              size={25}
              onClick={e => {
                e.stopPropagation()
                handleLog(record.key)
              }}
              className="view-icon"
            />
          )} */}
          </div>
        ),
      })
      return list
    }

    const onChange = async (_pagination, filters, sorter) => {
      let direction = ''
      if (sorter.order === 'ascend') direction = 'ASC'
      if (sorter.order === 'descend') direction = 'DESC'

      // Convert Ant Design filters to our columnFilters format
      const columnFilters = {}
      Object.keys(filters).forEach(key => {
        const filterValue = filters[key]
        if (filterValue && filterValue.length > 0) {
          // Find column configuration to determine filter type
          const column = columns.find(col => col.key === key)
          if (column) {
            if (column.type === 'text') {
              columnFilters[key] = { value: filterValue[0], operator: 'like' }
            } else if (column.type === 'date') {
              columnFilters[key] = { value: filterValue[0], operator: 'between' }
            } else if (column.type === 'number') {
              // Handle number range [min, max]
              if (Array.isArray(filterValue[0]) && filterValue[0].length === 2) {
                columnFilters[key] = { value: filterValue[0], operator: 'between' }
              } else {
                columnFilters[key] = { value: filterValue[0], operator: 'eq' }
              }
            } else if (column.type === 'select') {
              columnFilters[key] = { value: filterValue, operator: 'in' }
            } else {
              // Default to text search
              columnFilters[key] = { value: filterValue[0], operator: 'like' }
            }
          }
        }
      })

      setFilter({
        ...filter,
        sortDirection: direction,
        sortColumn: sorter.field,
        columnFilters: columnFilters,
        filters: {}, // Clear old filters
      })
    }

    const onSearch = value => {
      setFilter({
        ...filter,
        search: value?.trim(),
      })
      // Reset to page 1 when searching
      setPagination(prev => ({
        ...prev,
        current: 1,
      }))
    }

    const getPaginationUrl = skip => {
      let url = itemUrl
      if (pagination.pageSize) url += `?limit=${pagination?.pageSize}`
      if (pagination.current) url += `&page=${pagination.current}`
      if (filter.search) {
        url += `&search=${filter.search}`
        // Build searchBy parameter with all searchable column names
        const searchableColumns = columns
          .filter(col => col.type === 'text' || col.type === 'select' || !col.type) // Include text, select, and unknown types
          .map(col => col.name || col.key)
          .filter(name => name) // Remove empty names

        if (searchableColumns.length > 0) {
          url += `&searchBy=${searchableColumns.join(',')}`
        } else if (skip) {
          url += `&searchBy=${['true']}`
        }
      } else if (skip) {
        url += `&searchBy=${['true']}`
      }

      if (filter.sortColumn && filter.sortDirection) {
        let key = filter.sortColumn
        if (key.includes('Id')) key = key.slice(0, key.length - 2) + '.name'
        url += `&sortBy=${key}:${filter.sortDirection}`
      }

      // Handle legacy filters (from table column filters)
      if (Object.keys(filter.filters).length) {
        Object.keys(filter.filters).forEach(key => {
          let ids = filter.filters[key]
          if (!Array.isArray(ids)) return
          if (key.endsWith('Id')) {
            const slicedKey = key.slice(0, key.length - 2)
            url += `&filter.${slicedKey}.id=$in:${ids.join(',')}`
          } else {
            url += `&filter.${key}=$in:${ids.join(',')}`
          }
        })
      }

      // Handle new column filters
      if (Object.keys(filter.columnFilters).length) {
        Object.keys(filter.columnFilters).forEach(key => {
          const filterData = filter.columnFilters[key]
          if (!filterData || !filterData.value) return

          const { value, operator } = filterData
          let filterKey = key

          // Handle relationship fields (ending with Id)
          if (key.endsWith('Id')) {
            const slicedKey = key.slice(0, key.length - 2)
            filterKey = `${slicedKey}.id`
          }

          switch (operator) {
            case 'like':
              url += `&filter.${filterKey}=$ilike:${encodeURIComponent(value)}`
              break
            case 'eq':
              url += `&filter.${filterKey}=$eq:${encodeURIComponent(value)}`
              break
            case 'gt':
              url += `&filter.${filterKey}=$gt:${encodeURIComponent(value)}`
              break
            case 'gte':
              url += `&filter.${filterKey}=$gte:${encodeURIComponent(value)}`
              break
            case 'lt':
              url += `&filter.${filterKey}=$lt:${encodeURIComponent(value)}`
              break
            case 'lte':
              url += `&filter.${filterKey}=$lte:${encodeURIComponent(value)}`
              break
            case 'in':
              if (Array.isArray(value) && value.length > 0) {
                url += `&filter.${filterKey}=$in:${value.map(v => encodeURIComponent(v)).join(',')}`
              }
              break
            case 'between':
              if (Array.isArray(value) && value.length === 2) {
                const [min, max] = value
                if (key === 'depunere' || key === 'clarificari') {
                  // Handle date range
                  if (min && max) {
                    url += `&filter.${filterKey}=$gte:${encodeURIComponent(min)}&filter.${filterKey}=$lte:${encodeURIComponent(max)}`
                  } else if (min) {
                    url += `&filter.${filterKey}=$gte:${encodeURIComponent(min)}`
                  } else if (max) {
                    url += `&filter.${filterKey}=$lte:${encodeURIComponent(max)}`
                  }
                } else {
                  // Handle number range
                  if (min !== null && max !== null) {
                    url += `&filter.${filterKey}=$gte:${encodeURIComponent(min)}&filter.${filterKey}=$lte:${encodeURIComponent(max)}`
                  } else if (min !== null) {
                    url += `&filter.${filterKey}=$gte:${encodeURIComponent(min)}`
                  } else if (max !== null) {
                    url += `&filter.${filterKey}=$lte:${encodeURIComponent(max)}`
                  }
                }
              }
              break
            default:
              url += `&filter.${filterKey}=$eq:${encodeURIComponent(value)}`
          }
        })
      }

      // Handle custom filter parameters
      if (customFilterParams && customFilterUrlBuilder) {
        url = customFilterUrlBuilder(url, customFilterParams)
      }

      return url
    }

    const transferData = {
      data: permissions,
      targetKeys: targetKeys,
      selectedKeys: selectedKeys,
      onChange(nextTargetKeys) {
        /**Maps through nextTargetKeys and sets the target keys state to the permission at the indexes from nextTargetKeys*/
       // const permissionsKeys = nextTargetKeys.map((_, key) => permissions.indexOf(permissions[key]))

       // setTargetKeys(permissionsKeys)
	   setTargetKeys(nextTargetKeys)
      },
      /**Controls the behaviour of the selected item when they are changed */
      onSelectChange(sourceSelectedKeys, targetSelectedKeys) {
        setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys])
      },
      render(item) {
        return item.title
      },
    }

    const handleCancelModal = () => {
      setCreateModal(false)
      setTargetKeys(null)
    }

    return (
      <CCard className="p-3 margin-bottom-container">
        <div className="header-container">
          <h3>{languageContext.t(items)}</h3>
          <div className="search-and-add-container">
            {/* Refresh button added here */}
            {!hideRefresh && (
              <Button
                style={{ marginRight: 8 }}
                onClick={() => setRefreshTable(prev => prev + 1)}
                icon={<FaSpinner />}
                title={languageContext.t('Refresh')}
              >
                {languageContext.t('Refresh')}
              </Button>
            )}
            <Search
              placeholder={languageContext.t('Search') + ' (toate câmpurile)'}
              onSearch={onSearch}
              enterButton
              allowClear
              style={{ width: 300 }}
            />
            <Button
              onClick={() => {
                setCreateModal(true)
                setEntityId(null)
              }}
              className={buttons.create ? 'dyn-btn-primary' : 'hide-content'}
            >
              {languageContext.t('Add')} {languageContext.t(item)}
            </Button>
            {/* Extra header buttons injected from parent */}
            {extraHeaderButtons}
          </div>
        </div>

        {/* Custom filters row, if provided */}
        {customFilters && (
          <div className="custom-filters-row" style={{ margin: '16px 0' }}>
            {customFilters}
          </div>
        )}

        <div className="table-with-filters">
          {renderRowContent ? (
            // Custom rendering with row content under each row
            <Table
              bordered
              rowSelection={rowSelection}
              columns={getTableColumns(columns)}
              dataSource={displayedData}
              pagination={false}
              rowKey={record => record.key}
              onChange={onChange}
              className={`dyn-table${onRowSelect ? ' selectable' : ''} ${styles.customTable}`}
              rowClassName={rowClassName}
              expandable={
                renderExpandedRow
                  ? {
                      expandedRowRender: renderExpandedRow,
                      expandRowByClick: true,
                    }
                  : undefined
              }
              onRow={(record, _index) => {
                if (onRowSelect)
                  return {
                    onClick: () => {
                      const rowToSelect = document.querySelector(`.ant-table-row[data-row-key="${record.key}"]`)
                      // check if rowToSelect has .ant-table-row-selected class
                      if (rowToSelect.classList.contains('ant-table-row-selected')) {
                        rowToSelect.classList.remove('ant-table-row-selected')
                        onRowSelect(null)
                        return
                      }
                      const selectedRows = document.querySelectorAll('.ant-table-row-selected')
                      Array.from(selectedRows).forEach(row => {
                        row.classList.remove('ant-table-row-selected')
                      })
                      rowToSelect.classList.add('ant-table-row-selected')
                      onRowSelect(record)
                    },
                  }
                return {}
              }}
              components={{
                body: {
                  wrapper: ({ children, ...props }) => (
                    <tbody {...props}>
                      {React.Children.map(children, (child, index) => {
                        if (React.isValidElement(child)) {
                          const record = displayedData[index-1] // Adjust index for header row
                          const rowClassFromParent = rowClassName ? rowClassName(record) : ''
                          // Calculate total columns including selection column
                          let totalColumns = getTableColumns(columns).length + 1
                          return (
                            <React.Fragment key={child.key || index-1}>
                              {child}
                              <tr className="custom-row-content-row">
                                <td
                                  colSpan={totalColumns}
                                  className={`custom-row-content ${rowClassFromParent}`}
                                  style={{ padding: 0, paddingLeft: 10, margin: 0 }}
                                >
                                  {renderRowContent(record)}
                                </td>
                              </tr>
                            </React.Fragment>
                          )
                        }
                        return child
                      })}
                    </tbody>
                  ),
                },
              }}
            />
          ) : (
            // Standard table rendering
            <Table
              bordered
              rowSelection={rowSelection}
              columns={getTableColumns(columns)}
              dataSource={displayedData}
              pagination={false}
              rowKey={record => record.key}
              onChange={onChange}
              className={`dyn-table${onRowSelect ? ' selectable' : ''} ${styles.customTable}`}
              rowClassName={rowClassName}
              // scroll={{ x: 'max-content' }}
              expandable={
                renderExpandedRow
                  ? {
                      expandedRowRender: renderExpandedRow,
                      expandRowByClick: true,
                    }
                  : undefined
              }
              onRow={(record, _index) => {
                if (onRowSelect)
                  return {
                    onClick: () => {
                      const rowToSelect = document.querySelector(`.ant-table-row[data-row-key="${record.key}"]`)
                      // check if rowToSelect has .ant-table-row-selected class
                      if (rowToSelect.classList.contains('ant-table-row-selected')) {
                        rowToSelect.classList.remove('ant-table-row-selected')
                        onRowSelect(null)
                        return
                      }
                      const selectedRows = document.querySelectorAll('.ant-table-row-selected')
                      Array.from(selectedRows).forEach(row => {
                        row.classList.remove('ant-table-row-selected')
                      })
                      rowToSelect.classList.add('ant-table-row-selected')
                      onRowSelect(record)
                    },
                  }
                return {}
              }}
            />
          )}
        </div>
        <Pagination
          className="form-pagination"
          defaultCurrent={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          onChange={(page, pageSize) => {
            setPagination(prevPagination => ({
              ...prevPagination,
              current: page,
              pageSize: pageSize,
            }))
          }}
        />
        {showDeleteModal && (
          <Modal
            title={`${languageContext.t('Delete')} ${languageContext.t(item)}`}
            open={showDeleteModal}
            onCancel={() => setDeleteModal(false)}
            onOk={() => handleDelete(entityId)}
            className="dyn-form-modal"
          >
            <p>{languageContext.t('DeleteMessage')}</p>
          </Modal>
        )}
        {(showCreateModal || showUpdateModal) && (
          <Modal
            zIndex={1100}
            title={
              showCreateModal ? `${languageContext.t('Add')} ${languageContext.t(item)}` : `${languageContext.t('Edit')} ${languageContext.t(item)}`
            }
            open={showCreateModal || showUpdateModal}
            onCancel={() => {
              setEntityId(null)
              handleCancelModal(false)
              setUpdateModal(false)
            }}
            centered
            footer={null}
            width={1000}
            maskClosable={false}
            className="dyn-form-modal"
          >
            <Form layout="vertical" onFinish={save} form={form}>
              <Content className="row">
                <FormInputs inputs={inputs} transferData={transferData} form={form} />
              </Content>

              <Space className="justify-content-between mt-4" style={{ display: 'flex' }}>
                <Flex justify="flex-start" className="form-footer-buttons">
                  <Form.Item>
                    {showCreateModal && (
                      <Button className="dyn-btn-primary" htmlType="submit">
                        {languageContext.t('Submit')}
                      </Button>
                    )}
                    {showUpdateModal && (
                      <DropdownButton
                        icon={<DownOutlined />}
                        className=" dyn-btn-primary"
                        menu={{
                          items: [
                            {
                              label: languageContext.t('Clone'),
                              key: 'clone',
                            },
                          ],
                          onClick: () => {
                            setEntityId(null)
                            form.setFieldValue('name', form.getFieldValue('name') + ' - Clone')
                            /**If the form contains permissions, set the permissions from which is cloned to the new clone*/
                            if (form.getFieldsValue()?.permissions) form.setFieldValue('permissions', targetKeys)

                            form.submit()
                          },
                        }}
                        onClick={() => form.submit()}
                      >
                        {languageContext.t('Submit')}
                      </DropdownButton>
                    )}
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="default"
                      className="dyn-btn-secondary"
                      onClick={() => {
                        handleCancelModal(false)
                        setUpdateModal(false)
                      }}
                    >
                      {languageContext.t('Cancel')}
                    </Button>
                  </Form.Item>
                </Flex>
              </Space>
            </Form>
          </Modal>
        )}
        <style jsx>{`
          .rotating {
            animation: rotating 2s linear infinite;
          }
          @keyframes rotating {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          .table-with-filters {
            position: relative;
          }
          .dyn-table .ant-table-thead > tr > th {
            position: sticky;
            top: 0;
            z-index: 10;
            background: white;
          }
          .custom-row-content {
            padding: 8px 16px;
            background-color: inherit;
          }
          .custom-row-content-row {
            border-top: none !important;
          }
          .custom-row-content-row td {
            border-left: 1px solid #f0f0f0 !important;
            border-right: 1px solid #f0f0f0 !important;
            border-bottom: 1px solid #f0f0f0 !important;
            border-top: none !important;
          }
          /* Match background colors based on row status applied directly */
          .custom-row-content.row-status-analiza {
            background-color: transparent !important;
          }
          .custom-row-content.row-status-go {
            background-color: rgba(82, 196, 26, 0.1) !important;
          }
          .custom-row-content.row-status-no-go {
            background-color: rgba(119, 51, 51, 0.1) !important;
          }
          .custom-row-content.row-status-depusa {
            background-color: rgba(250, 173, 20, 0.1) !important;
          }
          .custom-row-content.row-status-sedinta {
            background-color: rgba(20, 204, 250, 0.1) !important;
          }
          .custom-row-content.row-status-anulata {
            background-color: rgba(119, 51, 51, 0.1) !important;
          }
          .custom-row-content.row-status-clarificari {
            background-color: rgba(250, 173, 20, 0.1) !important;
          }
          .custom-row-content.row-status-atribuita {
            background-color: rgba(82, 196, 26, 0.1) !important;
          }
          .custom-row-content.row-status-castigatoare {
            background-color: rgba(82, 196, 26, 0.1) !important;
          }
          .custom-row-content.row-status-necastigatoare {
            background-color: rgba(119, 51, 51, 0.1) !important;
          }
        `}</style>

        {/* SEAP popup and button removed; should be handled in parent via extraHeaderButtons */}
      </CCard>
    )
  }
)

export default TableContent
